import type { TelemetryClient } from "../core/client.js";
import { resolveRuntime } from "../core/runtime.js";
import type { TelemetryModule } from "../di/define-module.js";
import type { TelemetryPlatform } from "../platforms/types.js";
import type { TelemetryRuntimeInput } from "../schema/runtime.js";
import { createModuleShell } from "../setup/module-shell.js";
import type { PlatformBuildContext } from "../setup/platform-context.js";
import {
  attachVue2Bindings,
  type Vue2TelemetryBindings,
} from "./vue2-di.js";

/** Vue 2 constructor type (avoids hard dependency on vue package types) */
export interface Vue2Constructor {
  config: {
    errorHandler?: (
      err: Error,
      vm: unknown,
      info: string,
    ) => void;
  };
}

export interface Vue2TelemetryOptions {
  client: TelemetryClient;
  Vue: Vue2Constructor;
  runtime?: TelemetryRuntimeInput;
}

export function installVue2Telemetry({
  client,
  Vue,
  runtime = "auto",
}: Vue2TelemetryOptions): () => void {
  const resolved = resolveRuntime(runtime);
  client.setFramework("vue2");

  if (resolved === "server") {
    return () => undefined;
  }

  const previous = Vue.config.errorHandler;

  Vue.config.errorHandler = (err, vm, info) => {
    client.captureError(err, {
      framework: "vue2",
      handled: true,
      metadata: {
        vueInfo: info,
        componentName: getVue2ComponentName(vm),
      },
    });
    previous?.(err, vm, info);
  };

  return () => {
    Vue.config.errorHandler = previous;
  };
}

function getVue2ComponentName(vm: unknown): string | undefined {
  if (!vm || typeof vm !== "object") return undefined;
  const options = (vm as { $options?: { name?: string; _componentTag?: string } })
    .$options;
  return options?.name ?? options?._componentTag;
}

export interface Vue2TelemetryModule extends TelemetryModule, Vue2TelemetryBindings {}

export const vue2: TelemetryPlatform<Vue2TelemetryModule> = {
  id: "vue2",
  buildModule(ctx: PlatformBuildContext<Vue2TelemetryModule>) {
    return attachVue2Bindings(createModuleShell(ctx));
  },
};
